import type { Job } from "@domain/CRM/job";
import type { IJobIssues } from "@ports/jobs/IJobIssues";

export interface AgrianIssuesUseCasesDeps {
	jobIssuesApi: IJobIssues;
}

/**
 * Drives the Admin → Agrian Issues page. Orchestrates only the IO half
 * of the workflow (fetch dirty jobs, kick off a backend fix). The vm keeps
 * the React-only concerns: a per-id "currently fixing" set, the countdown
 * timer that the user watches while the backend catches up, and StrictMode
 * dedup for the initial fetch.
 *
 * The "fix → sleep → refetch" sequencing lives in the vm because the
 * `sleep` step is rendered as a visible countdown and therefore couples to
 * React state. If we ever needed a CLI-portable fix-and-wait flow, the
 * sleep duration would become a Deps and that orchestration would move
 * here — for now keeping it split lets each layer own what it's best at.
 */
export interface AgrianIssuesUseCases {
	/** GET /jobs/issues/agrian — list of jobs whose Agrian sync failed or
	 *  never completed. Throws on transport errors. */
	loadIssues(): Promise<Job[]>;
	/** POST /jobs/issues/agrian — kick off a backend retry for these jobs.
	 *  Returns immediately; the backend processes asynchronously. Caller
	 *  should wait some seconds before refetching to see results. */
	fix(ids: string[]): Promise<void>;
}

export function loadIssues(deps: AgrianIssuesUseCasesDeps): Promise<Job[]> {
	return deps.jobIssuesApi.getAgrian();
}

export async function fix(deps: AgrianIssuesUseCasesDeps, ids: string[]): Promise<void> {
	await deps.jobIssuesApi.fixAgrian(ids);
}

export function makeAgrianIssuesUseCases(deps: AgrianIssuesUseCasesDeps): AgrianIssuesUseCases {
	return {
		loadIssues: () => loadIssues(deps),
		fix: (ids) => fix(deps, ids),
	};
}
